import type { RouterHistory } from "@tanstack/react-router";
import { useSyncExternalStore } from "react";
import { scoreReturnPath } from "../../shared/navigation";

const ENTRY = "scoreReturnTo";
const HANDOFF = "score.navigation-handoff";
const listeners = new Set<() => void>();
let target: string | null = null;
const emit = () => {
  for (const listen of listeners) listen();
};
const subscribe = (listen: () => void) => {
  listeners.add(listen);
  return () => {
    listeners.delete(listen);
  };
};
export const subscribeBackTarget = subscribe;
export const backTarget = () => target;
export const useBackTarget = () =>
  useSyncExternalStore(
    subscribe,
    () => target,
    () => null,
  );

/** History metadata belongs to the browser ENTRY, not a page referrer or a
 * global previous URL. PUSH records the preceding page, REPLACE retains its
 * parent, POP restores that entry's parent. Reloads retain it too.
 *
 * Native anchors still load full documents in SCORE. Only a normal internal
 * click followed by pagehide hands off a parent to the next document. A new
 * tab, address-bar visit, auth callback or external link gets no guessed Back.
 * No credential data is recorded; denied sessionStorage simply uses fallback.
 */
export function installBackNavigation(history: RouterHistory): () => void {
  const origin = window.location.origin;
  const path = (value: unknown) => scoreReturnPath(value, origin);
  const here = () => path(window.location.href);
  const entry = (): string | null =>
    path((window.history.state as Record<string, unknown> | null)?.[ENTRY]);
  let previous = here();
  let candidate: { from: string; to: string; at: number } | null = null;
  target = entry();
  try {
    const raw = sessionStorage.getItem(HANDOFF);
    sessionStorage.removeItem(HANDOFF);
    if (raw !== null && target === null) {
      const next: unknown = JSON.parse(raw);
      if (typeof next === "object" && next !== null) {
        const h = next as Record<string, unknown>;
        const from = path(h["from"]);
        if (
          from !== null &&
          path(h["to"]) === here() &&
          typeof h["at"] === "number" &&
          Date.now() - h["at"] >= 0 &&
          Date.now() - h["at"] < 15_000 &&
          window.history.length > 1
        )
          target = from;
      }
    }
  } catch {
    /* Storage may be disabled in a WebView. */
  }

  const save = () => {
    // Flush TanStack's queued URL write before attaching metadata. Its
    // history adapter ignores our metadata-only replace so it
    // cannot recursively turn this into another route transition.
    history.flush();
    const ignored = history._ignoreSubscribers;
    history._ignoreSubscribers = true;
    try {
      window.history.replaceState({ ...window.history.state, [ENTRY]: target }, "");
    } finally {
      if (ignored === undefined) delete history._ignoreSubscribers;
      else history._ignoreSubscribers = ignored;
    }
    emit();
  };
  save();
  const unsubscribe = history.subscribe(({ location, action }) => {
    const next = path(location.href);
    if (action.type === "PUSH") target = previous;
    else if (action.type !== "REPLACE") target = candidate?.to === next ? candidate.from : entry();
    candidate = null;
    // A redirect replacing a sign-in page must not restore its credentials.
    if (next === null) target = null;
    previous = next;
    save();
  });
  const click = (event: MouseEvent) => {
    candidate = null;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (
      !(anchor instanceof HTMLAnchorElement) ||
      anchor.hasAttribute("download") ||
      (anchor.target !== "" && anchor.target !== "_self")
    )
      return;
    const from = here();
    const to = path(anchor.href);
    if (from !== null && to !== null && from !== to) candidate = { from, to, at: Date.now() };
  };
  const hide = () => {
    try {
      if (candidate !== null && Date.now() - candidate.at < 15_000)
        sessionStorage.setItem(HANDOFF, JSON.stringify(candidate));
      else sessionStorage.removeItem(HANDOFF);
    } catch {
      /* Native links retain the named fallback without storage. */
    }
    candidate = null;
  };
  const show = () => {
    previous = here();
    target = entry();
    emit();
  };
  document.addEventListener("click", click);
  window.addEventListener("pagehide", hide);
  window.addEventListener("pageshow", show);
  return () => {
    unsubscribe();
    document.removeEventListener("click", click);
    window.removeEventListener("pagehide", hide);
    window.removeEventListener("pageshow", show);
  };
}

const dismissals: (() => void)[] = [];
export const hasBackDismissal = () => dismissals.length > 0;
export function registerBackDismissal(close: () => void): () => void {
  dismissals.push(close);
  emit();
  return () => {
    const i = dismissals.lastIndexOf(close);
    if (i >= 0) dismissals.splice(i, 1);
    emit();
  };
}
export function dismissForBack(): boolean {
  const close = dismissals.at(-1);
  if (!close) return false;
  close();
  return true;
}
